import {useCallback, useMemo} from 'react'
import {useHistory as useHistoryOrigin} from 'react-router'
import {Routes} from 'types/commons'
import {RouteParam, ROUTE_MAP} from '../../../routes'
import {getRoutePath} from './RouteHelper'

export function useHistory<K extends Routes>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _route?: K,
) {
  const history = useHistoryOrigin<RouteParam[K]>()

  const getParamPath = useCallback(
    <P extends Routes>(path: P, recursivePath: string) => {
      const routeMapEntry = ROUTE_MAP[path]
      return routeMapEntry.path.replace(/:\w+/g, recursivePath)
    },
    [],
  )

  const handlePush = useCallback(
    <P extends Routes>(path: P, state, recursivePath?: string) => {
      if (recursivePath) {
        const route = getParamPath(path, recursivePath)
        history.push(route, state)
        return
      }
      history.push(getRoutePath(path), state)
    },
    [getParamPath, history],
  )

  const handleReplace = useCallback(
    <P extends Routes>(path: P, state, recursivePath?: string) => {
      if (recursivePath) {
        const route = getParamPath(path, recursivePath)
        history.replace(route, state)
        return
      }
      history.replace(getRoutePath(path), state)
    },
    [getParamPath, history],
  )

  return useMemo(
    () => ({
      ...history,
      push: handlePush,
      replace: handleReplace,
    }),
    [history, handlePush, handleReplace],
  )
}
